import { AlarmConfiguration } from 'domain/alarm-configuration'
import { AlarmCriteria } from 'domain/alarm-criteria'
import { AlarmConfigurationType } from 'enums/alarm-configuration-type'
import {
  getConfigurationTypeLabel,
} from 'features/alarms/types/alarm-configuration-type'
import { FilterMatchMode } from 'primereact/api'
import { Button } from 'primereact/button'
import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { InputText } from 'primereact/inputtext'
import { Toolbar } from 'primereact/toolbar'
import { useState } from 'react'
import { getCriterionDisplayValue, getCriterionTypeLabel } from '../config/criterion-config'
import './alarm-configurations-table.scss'

interface AlarmConfigurationsTableProps {
  alarmConfigurations: AlarmConfiguration[]
  totalConfigurations: number
  isLoading: boolean
  onNewConfiguration: () => void
  onDeleteConfiguration: (configuration: AlarmConfiguration) => void
}

const AlarmConfigurationsTable: React.FC<AlarmConfigurationsTableProps> = ({
  alarmConfigurations,
  totalConfigurations,
  isLoading,
  onNewConfiguration,
  onDeleteConfiguration,
}) => {
  const [configFilters, setConfigFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    type: { value: null, matchMode: FilterMatchMode.EQUALS },
    createdBy: { value: null, matchMode: FilterMatchMode.CONTAINS },
  })
  const [globalConfigFilter, setGlobalConfigFilter] = useState<string>('')

  const formatDate = (date: Date): string => {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  const categoryBodyTemplate = (rowData: AlarmConfiguration) => {
    const isAHCR = rowData.type === AlarmConfigurationType.AHCR

    return (
      <span
        className={`alarms-type-badge ${isAHCR ? 'alarms-type-plate' : 'alarms-type-color'
          }`}
      >
        <i className={`pi ${isAHCR ? 'pi-user' : 'pi-car'}`} />
        {getConfigurationTypeLabel(rowData.type)}
      </span>
    )
  }

  const criteriaBodyTemplate = (rowData: AlarmConfiguration) => {
    if (!rowData.criterias?.length) {
      return (
        <span className='alarm-no-criteria'>
          Nenhum critério
        </span>
      )
    }

    return (
      <div className='alarm-criteria-container'>
        {rowData.criterias.map((criteria: AlarmCriteria) => {
          const label = getCriterionTypeLabel(criteria.type)
          return (
            <div
              key={`${criteria.type}-${criteria.value}`}
              className='alarm-criterion-badge'
            >
              <span className='alarm-criterion-label'>
                {label}
              </span>
              <span className='alarm-criterion-value'>
                {getCriterionDisplayValue(criteria.value)}
              </span>
            </div>
          )
        })}
      </div>
    )
  }

  const dateBodyTemplate = (rowData: AlarmConfiguration) => {
    return (
      <div className='alarms-date-cell'>
        <i className='pi pi-calendar alarms-date-icon' />
        <span>{formatDate(rowData.createdDate)}</span>
      </div>
    )
  }

  const createdByBodyTemplate = (rowData: AlarmConfiguration) => {
    return (
      <div className='alarms-user-cell'>
        <i className='pi pi-user alarms-user-icon' />
        <span>{rowData.createdBy}</span>
      </div>
    )
  }

  const actionBodyTemplate = (rowData: AlarmConfiguration) => {
    return (
      <div className='alarms-actions'>
        <Button
          icon='pi pi-trash'
          rounded
          className='alarms-action-delete'
          onClick={() => onDeleteConfiguration(rowData)}
          tooltip='Excluir Configuração'
          tooltipOptions={{ position: 'top' }}
        />
      </div>
    )
  }

  const configurationToolbar = (
    <div className='alarms-toolbar-content'>
      <div className='alarms-toolbar-left'>
        <h4 className='alarms-toolbar-title'>
          <i className='pi pi-cog' />
          Configurações de Alarme
        </h4>
      </div>
      <div className='alarms-toolbar-right ml-2'>
        <Button
          label='Novo Alarme'
          icon='pi pi-plus'
          className='alarms-btn-new'
          onClick={onNewConfiguration}
          loading={isLoading}
        />
      </div>
    </div>
  )

  return (
    <>
      <Toolbar
        className='alarms-toolbar'
        start={configurationToolbar}
      />
      <DataTable
        value={alarmConfigurations}
        paginator
        rows={10}
        rowsPerPageOptions={[5, 10, 25]}
        dataKey='id'
        responsiveLayout='scroll'
        filters={configFilters}
        globalFilterFields={['type', 'createdBy']}
        emptyMessage='Nenhuma configuração de alarme encontrada.'
        className='alarms-datatable'
        stripedRows
        showGridlines
        loading={isLoading}
        header={
          <div className='alarms-table-header'>
            <h4 className='alarms-table-title'>
              <i className='pi pi-list' />
              Lista de Configurações ({totalConfigurations})
            </h4>
            <div className='alarms-search-group'>
              <span className='alarms-search-icon'>
                <i className='pi pi-search' />
              </span>
              <InputText
                value={globalConfigFilter}
                onChange={(e) => {
                  setGlobalConfigFilter(e.target.value)
                  const _filters = { ...configFilters }
                  // @ts-ignore
                  _filters['global'].value = e.target.value
                  setConfigFilters(_filters)
                }}
                placeholder='Pesquisar configurações...'
                className='alarms-search-input'
              />
            </div>
          </div>
        }
      >
        <Column
          field='type'
          header='Categoria'
          body={categoryBodyTemplate}
          sortable
          className='alarms-type-column'
        />
        <Column
          header='Critérios'
          body={criteriaBodyTemplate}
          className='alarms-criteria-column'
        />
        <Column
          field='createdDate'
          header='Data de Criação'
          body={dateBodyTemplate}
          sortable
          className='alarms-date-column'
        />
        <Column
          field='createdBy'
          header='Criado Por'
          body={createdByBodyTemplate}
          sortable
          className='alarms-user-column'
        />
        <Column
          body={actionBodyTemplate}
          header='Ações'
          exportable={false}
          className='alarms-actions-column'
        />
      </DataTable>
    </>
  )
}

export default AlarmConfigurationsTable
